import styled from "styled-components";
import { Modal, ModalBody, ModalView } from "../styles";

export const ForwardModal = styled( Modal )`
  width: min( 680px, calc(100vw - 24px) );
  max-width: 680px;
`;

export const ForwardModalView = styled( ModalView )`
  max-height: min( 760px, calc(100vh - 32px) );
  border-radius: ${({ theme }) => theme.border_radius.large }px;
`;

export const ForwardModalBody = styled( ModalBody )`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.x_medium }px;

  & .simplebar-content {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_medium }px;
  }
`;

export const SourcePreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  border-left: 3px solid ${({ theme }) => theme.colors.info.default };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme }) => theme.colors.info.default }12;

  & > span {
    color: ${({ theme }) => theme.colors.text.secondary };
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 700;
    text-transform: uppercase;
  }

  & > p {
    margin: 0;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text.default };
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: 1.45;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  min-height: 40px;
  padding: ${({ theme }) => `${ theme.spacing.xx_small }px ${ theme.spacing.x_medium }px`};
  border: 1px solid ${({ theme }) => theme.palette.border };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  outline: none;
  background-color: ${({ theme }) => theme.palette.surface };
  color: ${({ theme }) => theme.colors.text.default };

  &:focus-visible {
    border-color: ${({ theme }) => theme.palette.primary };
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.primaryHover }44;
  }
`;

export const SelectionSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.x_small }px;

  & > strong {
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & > button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: 1px solid ${({ theme }) => theme.palette.border };
    border-radius: 999px;
    background: ${({ theme }) => theme.palette.surface };
    color: ${({ theme }) => theme.colors.text.default };
    cursor: pointer;
  }

  & > small {
    width: 100%;
    color: ${({ theme }) => theme.colors.warning.default };
  }
`;

export const RecipientList = styled.div`
  display: flex;
  min-height: 120px;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.x_small }px;
`;

export const RecipientItem = styled.button<{
  $selected: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  padding: ${({ theme }) => theme.spacing.x_small }px;
  border: 1px solid ${({ theme, $selected }) => $selected ? theme.colors.info.default : theme.palette.border };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme, $selected }) => $selected ? `${ theme.colors.info.default }14` : "transparent" };
  color: ${({ theme }) => theme.colors.text.default };
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: .45;
  }

  & > div.contact-copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
  }

  & > div.contact-copy > span,
  & > div.contact-copy > small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > div.contact-copy > small {
    color: ${({ theme }) => theme.colors.text.secondary };
  }
`;

export const Pagination = styled.div`
  & > ul {
    display: flex;
    justify-content: center;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  & a {
    display: flex;
    min-width: 30px;
    min-height: 30px;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.border_radius.small }px;
    color: ${({ theme }) => theme.colors.text.default };
    cursor: pointer;
  }

  & li.selected a {
    background-color: ${({ theme }) => theme.colors.info.default };
    color: ${({ theme }) => theme.colors.text.contrast.default };
  }

  & li.disabled a {
    cursor: default;
    opacity: .4;
  }
`;

export const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.x_small }px;

  & > h3 {
    margin: 0;
    font-size: ${({ theme }) => theme.font_size.xxx_small }px;
  }

  & > div {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: ${({ theme }) => theme.spacing.x_small }px;
    border-radius: ${({ theme }) => theme.border_radius.medium }px;
    background-color: ${({ theme }) => theme.palette.surface };
  }

  & > div.success > strong { color: ${({ theme }) => theme.colors.success.default }; }
  & > div.failed > strong { color: ${({ theme }) => theme.colors.error.default }; }
  & small { color: ${({ theme }) => theme.colors.text.secondary }; }
`;

export const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  padding-top: ${({ theme }) => theme.spacing.x_small }px;
  border-top: 1px solid ${({ theme }) => theme.palette.border };

  & > span {
    width: 100%;
    color: ${({ theme }) => theme.colors.error.default };
    font-size: ${({ theme }) => theme.font_size.x_small }px;
  }

  @media (max-width: 520px) {
    & > button { flex: 1; }
  }
`;
